import React, { useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Tooltip,
  Button,
  Dialog,
  DialogContent,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import styles from "../../../styles/vendorPurchaseOrder.module.css";

const ACCENT = "#0798f0";

function PdfPreviewDialog({ document, onClose }) {
  const po = document?.purchaseOrderId;
  return (
    <Dialog
      open={!!document}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: "90vh", borderRadius: "16px", overflow: "hidden" },
      }}
    >
      <div className="flex items-center justify-between border-b border-[#E5E7EB] bg-white px-4 py-2">
        <h2 className="text-lg font-semibold text-[#1F2937]">
          {po?.po_number || "Document"}
        </h2>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </div>
      <DialogContent className="!m-0 !p-0">
        {document?.pdfLink ? (
          <iframe
            src={document.pdfLink}
            title={document.po_number}
            className="h-full w-full"
          />
        ) : (
          <div className="flex h-full items-center justify-center">
            <p className="text-sm text-[#6B7280]">PDF not available</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DenseDocumentsTable({ documents, onViewStores }) {
  const navigate = useNavigate();
  const [previewDoc, setPreviewDoc] = useState(null);

  const showStoreCount = useMemo(
    () => documents.some((d) => d?.purchaseOrderId?.storeCount != null),
    [documents],
  );
  const showStatus = useMemo(
    () => documents.some((d) => d?.purchaseOrderId?.status),
    [documents],
  );

  if (!documents?.length) {
    return (
      <div className={styles.emptyState}>
        <p className={styles.emptyStateText}>No documents available</p>
      </div>
    );
  }

  return (
    <>
      <div>
        <div className=" mb-2 ">
          <ArrowBackIcon
            sx={{
              cursor: "pointer",
            }}
            onClick={() => {
              navigate(-1);
            }}
          />
        </div>
        <TableContainer
          component={Paper}
          variant="outlined"
          className={styles.documentsTableContainer}
        >
          <Table stickyHeader className={styles.documentsTable}>
            <TableHead>
              <TableRow className={styles.documentsTableHeader}>
                <TableCell className={styles.documentsTableHeaderCell}>
                  PO No.
                </TableCell>
                <TableCell className={styles.documentsTableHeaderCell}>
                  Grand Total
                </TableCell>
                <TableCell className={styles.documentsTableHeaderCell}>
                  Delivery Date
                </TableCell>
                <TableCell className={styles.documentsTableHeaderCell}>
                  No. Of Items
                </TableCell>

                {showStoreCount && (
                  <TableCell className={styles.documentsTableHeaderCell}>
                    Stores
                  </TableCell>
                )}
                {showStatus && (
                  <TableCell className={styles.documentsTableHeaderCell}>
                    Status
                  </TableCell>
                )}
                <TableCell
                  className={styles.documentsTableHeaderCell}
                  align="center"
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.map((document, index) => {
                const poNumber = document?.po_number || `Document ${index + 1}`;
                const purchaseOrderId = document?._id;
                const grandTotal = document?.rawData?.grand_total;
                const deliveryDate = document?.rawData?.doc_delivery_date
                  ? new Date(
                      document.rawData.doc_delivery_date,
                    ).toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                      timeZone: "Asia/Kolkata",
                    })
                  : "N/A";
                const noOfItems = document?.rawData?.no_of_items;

                return (
                  <TableRow key={document?._id || index} hover>
                    <TableCell className={styles.documentsTableCell}>
                      {poNumber}
                    </TableCell>
                    <TableCell className={styles.documentsTableCell}>
                      {grandTotal}
                    </TableCell>
                    <TableCell className={styles.documentsTableCell}>
                      {deliveryDate}
                    </TableCell>
                    <TableCell className={styles.documentsTableCell}>
                      {noOfItems}
                    </TableCell>
                    {showStoreCount && (
                      <TableCell>{document?.storeCount ?? "—"}</TableCell>
                    )}

                    <TableCell align="center" className={styles.actionsCell}>
                      <Tooltip title="Preview PDF">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => setPreviewDoc(document)}
                            disabled={!document?.pdfLink}
                          >
                            <VisibilityOutlinedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {onViewStores && purchaseOrderId && (
                        <Tooltip title="View Stores">
                          <IconButton
                            size="small"
                            onClick={() => onViewStores(purchaseOrderId)}
                          >
                            <StorefrontOutlinedIcon
                              fontSize="small"
                              sx={{ color: ACCENT }}
                            />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      <PdfPreviewDialog
        document={previewDoc}
        onClose={() => setPreviewDoc(null)}
      />
    </>
  );
}

export default function PdfViewer({
  open = false,
  onClose,
  documents = [],
  title = "Documents",
  mode = "page",
  onViewStores,
}) {
  console.log("Documents in PdfViewer:", documents);
  const content = (
    <div
      className={
        mode === "modal"
          ? "h-full overflow-y-auto bg-[#F3F4F6] p-5"
          : "  bg-[#F3F4F6] p-4"
      }
    >
      <div className="">
        <DenseDocumentsTable
          documents={documents}
          onViewStores={onViewStores}
        />
      </div>
    </div>
  );

  // MODAL MODE

  if (mode === "modal") {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: "90vh",
            borderRadius: "16px",
            overflow: "hidden",
          },
        }}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between border-b border-[#E5E7EB] bg-white px-6 py-4">
          <h2 className="text-lg font-semibold text-[#1F2937]">{title}</h2>

          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>

        <DialogContent className="!m-0 !p-0">{content}</DialogContent>
      </Dialog>
    );
  }

  // ROUTE MODE

  return (
    <div className=" bg-[#F3F4F6]">
      {/* Page Header */}
      {/* <div className=" border-b border-[#E5E7EB] bg-white px-6 py-4">
        <h1 className="text-xl font-semibold text-[#1F2937]">{title}</h1>
      </div> */}

      {content}
    </div>
  );
}
